import { ILot, ILotState, IStationTelemetryLot } from 'utils/interfaces'
import {
  humanizeSnakeCaseLabel,
  isNotSetLabel,
  maintenanceStageMap,
  occupiedStatusMap,
  statusTypeMap,
} from 'utils/lotInterfaces'

export const OCCUPIED_STATUS_OCCUPIED = 1
export const OCCUPIED_STATUS_RELEASED = 2
export const OCCUPIED_STATUS_PENDING = 3

export type LotLiveTelemetry = {
  occupiedStatus: number | null
  robotSn: string | null
  statusLabel: string
  statusErrorMsg: string | null
  maintenanceStageLabel: string
}

export type LotOccupancyHistoryFallback = Pick<ILotState, 'robot_sn' | 'occupied_status'> | null | undefined

export function resolveRobotSn(liveRobotSn: string | null | undefined, fallback?: string | number | null) {
  if (liveRobotSn) {
    return liveRobotSn
  }
  if (fallback != null && String(fallback) !== '' && String(fallback) !== '0') {
    return String(fallback)
  }
  return null
}

function formatStageLabel(lot: IStationTelemetryLot) {
  const label = lot.maintenance_stage_label
    ? humanizeSnakeCaseLabel(lot.maintenance_stage_label)
    : lot.maintenance_stage != null
      ? maintenanceStageMap[lot.maintenance_stage]
      : undefined
  if (!label || isNotSetLabel(label)) {
    return '—'
  }
  return label
}

function formatStatusLabel(status: number | null | undefined) {
  if (status == null || status === 0) {
    return '—'
  }
  return statusTypeMap[status] ?? humanizeSnakeCaseLabel(String(status))
}

export function parseLotTelemetryRow(lot: IStationTelemetryLot): LotLiveTelemetry {
  const occupiedStatus = lot.occupied_status ?? null
  const robotSn = lot.state.robot_sn != null && lot.state.robot_sn !== 0 ? String(lot.state.robot_sn) : null
  const errorMsg = lot.state.error?.error_msg?.trim() || null
  return {
    occupiedStatus,
    robotSn,
    statusLabel: formatStatusLabel(lot.state.status),
    statusErrorMsg: errorMsg || null,
    maintenanceStageLabel: formatStageLabel(lot),
  }
}

const RELEASED_LABEL = occupiedStatusMap[OCCUPIED_STATUS_RELEASED]
const OCCUPIED_LABEL = occupiedStatusMap[OCCUPIED_STATUS_OCCUPIED]
const PENDING_LABEL = occupiedStatusMap[OCCUPIED_STATUS_PENDING]

/**
 * Prefer live snapshot. History RELEASED never surfaces the last robot_sn
 * (flash bug on page open) — show Released instead.
 * PENDING: reserve in progress — show Pending, not robot SN (protocol §2.2).
 */
export function formatOccupiedOrRobot(live: LotLiveTelemetry | undefined, historyFallback?: LotOccupancyHistoryFallback) {
  if (live) {
    if (live.occupiedStatus === OCCUPIED_STATUS_RELEASED) {
      return RELEASED_LABEL
    }
    if (live.occupiedStatus === OCCUPIED_STATUS_PENDING) {
      return PENDING_LABEL
    }
    if (live.occupiedStatus === OCCUPIED_STATUS_OCCUPIED) {
      return resolveRobotSn(live.robotSn, null) ?? OCCUPIED_LABEL
    }
    return resolveRobotSn(live.robotSn, null) ?? RELEASED_LABEL
  }

  if (!historyFallback) {
    return RELEASED_LABEL
  }
  if (historyFallback.occupied_status === OCCUPIED_STATUS_RELEASED) {
    return RELEASED_LABEL
  }
  if (historyFallback.occupied_status === OCCUPIED_STATUS_PENDING) {
    return PENDING_LABEL
  }
  if (historyFallback.occupied_status === OCCUPIED_STATUS_OCCUPIED) {
    return resolveRobotSn(null, historyFallback.robot_sn) ?? OCCUPIED_LABEL
  }
  return RELEASED_LABEL
}

export function getOccupiedRobotSerialForLot(lot: ILot, live?: LotLiveTelemetry): string | null {
  if (live) {
    if (live.occupiedStatus === OCCUPIED_STATUS_RELEASED) {
      return null
    }
    if (live.occupiedStatus === OCCUPIED_STATUS_PENDING) {
      return null
    }
    if (live.occupiedStatus === OCCUPIED_STATUS_OCCUPIED) {
      return resolveRobotSn(live.robotSn, null)
    }
    return null
  }

  const latest = lot.state_history?.[0]
  if (!latest) {
    return null
  }
  if (latest.occupied_status === OCCUPIED_STATUS_RELEASED) {
    return null
  }
  if (latest.occupied_status === OCCUPIED_STATUS_PENDING) {
    return null
  }
  if (latest.occupied_status === OCCUPIED_STATUS_OCCUPIED) {
    return resolveRobotSn(null, latest.robot_sn)
  }
  // Legacy history without occupied_status: do not assume still occupied from robot_sn alone.
  return null
}

export function getOccupiedRobotSerialsForStation(
  stationLots: ILot[],
  liveByLotId?: Record<number, LotLiveTelemetry>
): string[] {
  const seen = new Set<string>()
  const serials: string[] = []
  for (const lot of stationLots) {
    const serial = getOccupiedRobotSerialForLot(lot, liveByLotId?.[lot.lot_id])
    if (serial && !seen.has(serial)) {
      seen.add(serial)
      serials.push(serial)
    }
  }
  return serials
}

/** Assets Status column: robot SN(s) when occupied; null when free / unknown. */
export function getStationOccupancyStatusText(
  stationLots: ILot[],
  liveByLotId?: Record<number, LotLiveTelemetry>
): string | null {
  if (!stationLots.length) {
    return null
  }
  const labels: string[] = []
  const seen = new Set<string>()
  for (const lot of stationLots) {
    const label = formatOccupiedOrRobot(liveByLotId?.[lot.lot_id], lot.state_history?.[0])
    // Assets Status: only robot SNs — skip free/Released/Pending placeholders.
    if (!label || label === '—' || label === RELEASED_LABEL || label === PENDING_LABEL || seen.has(label)) {
      continue
    }
    seen.add(label)
    labels.push(label)
  }
  if (!labels.length) {
    return null
  }
  return labels.join(', ')
}
